import React, { useEffect, useState } from "react";
import { PartnerLayout } from "./PartnerLayout";
import { StatusBadge } from "../admin/shared";
import partnerApi from "../../lib/partnerApi";
import { formatEUR, formatCrypto } from "../../lib/api";
import { TrendingUp, Receipt, Download, ArrowUpRight } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

export default function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [mode, setMode] = useState("volume"); // volume | orders

  useEffect(() => {
    partnerApi.get("/partner/dashboard").then((r) => setData(r.data)).catch(() => {});
    partnerApi.get("/partner/settlements").then((r) => setSettlements(r.data.settlements || [])).catch(() => {});
  }, []);

  if (!data) return <PartnerLayout title="Dashboard"><div className="text-sm text-muted-foreground">Loading…</div></PartnerLayout>;

  const max = Math.max(1, ...data.daily.map((d) => (mode === "volume" ? d.volume_eur : d.orders)));

  const downloadCsv = (s) => {
    const header = ["order_id", "asset", "amount_eur", "fee_eur", "completed_at"];
    const rows = (s.rows || []).map((r) => {
      const fee = (r.amount_eur * s.fee_bps / 10000).toFixed(2);
      return [r.order_id, r.asset, r.amount_eur, fee, r.completed_at].map((v) => JSON.stringify(v)).join(",");
    });
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `settlement-${s.month}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  return (
    <PartnerLayout title="Dashboard" subtitle="Volume, conversion, and revenue this month">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="dashboard-stats">
        <StatCard label="Volume / month" value={formatEUR(data.volume_month_eur)} icon={<TrendingUp className="w-3.5 h-3.5 text-primary" />} testid="stat-volume" />
        <StatCard label="Orders completed" value={data.orders_month.toLocaleString()} testid="stat-orders" />
        <StatCard label="Conversion" value={`${data.conversion_rate}%`} testid="stat-conversion" />
        <StatCard label={`Rev. share (${data.fee_bps} bps)`} value={formatEUR(data.revenue_share_month_eur)} highlight testid="stat-revenue" />
      </div>

      <div className="xch-card p-5 mb-5" data-testid="dashboard-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Last 30 days</div>
            <div className="font-display text-lg font-bold text-foreground">{mode === "volume" ? "Volume" : "Order count"}</div>
          </div>
          <div className="flex bg-muted rounded-lg p-0.5">
            <button onClick={() => setMode("volume")} className={`text-xs font-semibold px-3 py-1 rounded-md transition-colors ${mode === "volume" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid="chart-mode-volume">Volume</button>
            <button onClick={() => setMode("orders")} className={`text-xs font-semibold px-3 py-1 rounded-md transition-colors ${mode === "orders" ? "bg-white text-foreground shadow-sm" : "text-muted-foreground"}`} data-testid="chart-mode-orders">Orders</button>
          </div>
        </div>
        <svg viewBox="0 0 600 140" className="w-full h-32" preserveAspectRatio="none">
          <defs>
            <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#4F46FF" stopOpacity="0.35" />
              <stop offset="1" stopColor="#4F46FF" stopOpacity="0" />
            </linearGradient>
          </defs>
          {(() => {
            const points = data.daily.map((d, i) => {
              const x = (i / (data.daily.length - 1)) * 600;
              const val = mode === "volume" ? d.volume_eur : d.orders;
              const y = 130 - (val / max) * 120;
              return `${x},${y}`;
            });
            const path = points.join(" L ");
            const area = `M 0,140 L ${path} L 600,140 Z`;
            return (
              <>
                <path d={area} fill="url(#lg)" />
                <path d={`M ${path}`} fill="none" stroke="#4F46FF" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
              </>
            );
          })()}
        </svg>
        <div className="flex justify-between text-[10px] text-muted-foreground mt-1 font-mono">
          <span>{data.daily[0]?.date}</span>
          <span>{data.daily[data.daily.length - 1]?.date}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-5">
        <div className="xch-card overflow-hidden" data-testid="recent-orders">
          <div className="p-4 border-b border-border">
            <div className="font-display text-base font-bold text-foreground">Recent completed orders</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-muted/50 border-b border-border">
                <Th>Order</Th><Th>Asset</Th><Th right>Amount</Th><Th>Status</Th><Th>Date</Th>
              </tr></thead>
              <tbody>
                {data.recent_orders.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-xs text-muted-foreground">No completed orders yet.</td></tr>
                )}
                {data.recent_orders.map((o) => (
                  <tr key={o.id} className="border-b border-border last:border-0" data-testid={`recent-order-${o.id}`}>
                    <td className="px-3 py-2 font-mono text-[11px]">{o.id.slice(0, 8)}…</td>
                    <td className="px-3 py-2 font-display font-bold text-foreground">{o.asset}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-mono">{formatEUR(o.fiat_amount)}</td>
                    <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground tabular-nums">{new Date(o.created_at).toLocaleDateString("en-IE")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="xch-card overflow-hidden" data-testid="webhook-health">
          <div className="p-4 border-b border-border">
            <div className="font-display text-base font-bold text-foreground">Webhook delivery</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-muted/50 border-b border-border">
                <Th>Event</Th><Th right>HTTP</Th><Th right>Latency</Th><Th>Status</Th><Th>When</Th>
              </tr></thead>
              <tbody>
                {data.webhook_logs.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-xs text-muted-foreground">No webhook attempts yet.</td></tr>
                )}
                {data.webhook_logs.map((w) => (
                  <tr key={w.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-xs font-mono">{w.event || "—"}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono">{w.last_status || "—"}</td>
                    <td className="px-3 py-2 text-right text-xs font-mono tabular-nums">{w.latency_ms ? `${w.latency_ms}ms` : "—"}</td>
                    <td className="px-3 py-2"><StatusBadge status={w.status} /></td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground tabular-nums">{new Date(w.last_attempt_at).toLocaleTimeString("en-IE")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="xch-card overflow-hidden" data-testid="settlements">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Receipt className="w-4 h-4 text-primary" />
          <div className="font-display text-base font-bold text-foreground">Monthly settlements</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 border-b border-border">
              <Th>Month</Th><Th right>Volume</Th><Th right>Orders</Th><Th right>Revenue share</Th><Th />
            </tr></thead>
            <tbody>
              {settlements.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-xs text-muted-foreground">No settlements yet.</td></tr>
              )}
              {settlements.map((s) => (
                <tr key={s.month} className="border-b border-border last:border-0" data-testid={`settlement-${s.month}`}>
                  <td className="px-3 py-2 font-display font-bold">{s.month}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono">{formatEUR(s.volume_eur)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono">{s.orders_completed}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-mono font-semibold text-primary">{formatEUR(s.revenue_share_eur)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadCsv(s)} className="rounded-lg h-7 gap-1" data-testid={`download-${s.month}`}>
                      <Download className="w-3.5 h-3.5" /> CSV
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PartnerLayout>
  );
}

const StatCard = ({ label, value, icon, highlight, testid }) => (
  <div className={`xch-card p-4 ${highlight ? "ring-2 ring-primary/20" : ""}`} data-testid={testid}>
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
      {icon} {label}
    </div>
    <div className={`font-display text-2xl font-extrabold mt-1 tabular-nums ${highlight ? "text-primary" : "text-foreground"}`}>{value}</div>
  </div>
);

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground ${right ? "text-right" : "text-left"}`}>{children}</th>
);
